import React, { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { LogOut } from 'lucide-react';
import { apiGet, apiPatch } from '../../lib/api.js';
import { firstNameOf, resolveAlertState } from '../../lib/alertToggles.js';
import ThemeToggle from '../ThemeToggle.jsx';
import AlertToggleButtons from './AlertToggleButtons.jsx';

const QK = ['agent-notifications'];

/**
 * Account dropdown (under the header's Welcome button): "Welcome, <first name>", the four alert toggles, a
 * Light/Dark switch and Logout. Toggles save immediately (optimistic).
 * @param {{agent: object|null, open: boolean, onClose: () => void, onLogout: () => void}} props
 * @returns {JSX.Element|null}
 */
export default function UserMenu({ agent, open, onClose, onLogout }) {
  const qc = useQueryClient();
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: QK,
    queryFn: () => apiGet('/agents/me/notifications'),
    enabled: open,
    staleTime: 30_000,
  });
  const state = resolveAlertState(data?.notifications ?? agent?.notifications);

  const save = useMutation({
    mutationFn: (patch) => apiPatch('/agents/me/notifications', patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: QK });
      const prev = qc.getQueryData(QK);
      qc.setQueryData(QK, { ...(prev || {}), notifications: { ...(prev?.notifications || {}), ...patch } });
      return { prev };
    },
    onError: (err, _patch, ctx) => {
      qc.setQueryData(QK, ctx?.prev);
      toast.error(err?.response?.data?.error || 'Could not save alert setting');
    },
    onSuccess: (res) => { if (res?.notifications) qc.setQueryData(QK, res); },
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      // The Welcome trigger toggles the menu itself; closing here too would re-open it on click.
      if (e.target.closest?.('[data-testid="account-menu-trigger"]')) return;
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Account menu"
      data-testid="user-menu"
      className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-civix-border bg-civix-panel shadow-2xl p-3 space-y-3 z-50"
    >
      <div className="px-1 pt-1 text-sm font-semibold text-white">Welcome, {firstNameOf(agent) || 'there'}</div>

      <AlertToggleButtons
        state={state}
        onToggle={(key, next) => save.mutate({ [key]: next })}
      />

      <ThemeToggle />

      <button
        type="button"
        onClick={onLogout}
        className="w-full flex items-center gap-4 px-2 py-2 rounded-md text-sm font-medium text-white/80 hover:bg-white/5 transition cursor-pointer"
      >
        <LogOut className="w-5 h-5 text-rose-400" /> Logout
      </button>
    </div>
  );
}
